import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';
import useAdminDialog from '../components/AdminDialog';

const STATUS_BADGE = { pending: 'badge-blue', approved: 'badge-green', rejected: 'badge-red' };

export default function Submissions() {
  const { copy } = useAdminLanguage();
  const submissionsCopy = copy.submissions;
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const { confirmAction, dialog } = useAdminDialog();

  const fetchSubmissions = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/submissions', { params: { status: filter, keyword } })
      .then(res => setSubmissions(res.data.submissions || []))
      .catch(err => setError(err.response?.data?.error || submissionsCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [filter, keyword, submissionsCopy.errors.loadFailed]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const filtered = submissions.filter(s => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (keyword.trim()) {
      const q = keyword.trim().toLowerCase();
      return (s.task_title || '').toLowerCase().includes(q) ||
        (s.user_name || '').toLowerCase().includes(q) ||
        (s.content || '').toLowerCase().includes(q);
    }
    return true;
  });

  const approve = async (id) => {
    if (!await confirmAction({
      title: submissionsCopy.confirm.approve.title,
      message: submissionsCopy.confirm.approve.message,
    })) return;
    await api.post(`/admin/submissions/${id}/approve`);
    fetchSubmissions();
  };
  const reject = async (id) => {
    if (!await confirmAction({
      title: submissionsCopy.confirm.reject.title,
      message: submissionsCopy.confirm.reject.message,
    })) return;
    await api.post(`/admin/submissions/${id}/reject`);
    fetchSubmissions();
  };
  const remove = async (id) => {
    if (!await confirmAction({
      title: submissionsCopy.confirm.delete.title,
      message: submissionsCopy.confirm.delete.message,
      variant: 'danger',
    })) return;
    await api.delete(`/admin/submissions/${id}`);
    fetchSubmissions();
  };

  return (
    <div>
      {dialog}
      <div className="page-title">{submissionsCopy.title}</div>
      {error && <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>{error}</div>}
      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div className="search-input">
            <input placeholder={submissionsCopy.searchPlaceholder} value={keyword} onChange={e => setKeyword(e.target.value)} />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <option value="all">{submissionsCopy.allStatus}</option>
            <option value="pending">{submissionsCopy.status.pending}</option>
            <option value="approved">{submissionsCopy.status.approved}</option>
            <option value="rejected">{submissionsCopy.status.rejected}</option>
          </select>
          <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center', marginLeft: 'auto' }}>{submissionsCopy.showing(filtered.length, submissions.length)}</span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{submissionsCopy.columns.id}</th><th>{submissionsCopy.columns.task}</th><th>{submissionsCopy.columns.submitter}</th><th>{submissionsCopy.columns.content}</th><th>{submissionsCopy.columns.attachments}</th><th>{submissionsCopy.columns.status}</th><th>{submissionsCopy.columns.createdAt}</th><th>{submissionsCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{submissionsCopy.loading}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{submissionsCopy.empty}</td></tr>
              ) : filtered.map(s => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td style={{ fontWeight: 600 }}>{s.task_title || '-'}</td>
                  <td>{s.user_name || '-'}</td>
                  <td style={{ maxWidth: 260, color: '#555' }}>{s.content}</td>
                  <td>
                    {(Array.isArray(s.attachments) ? s.attachments : []).map(a => (
                      <span key={a} style={{ display: 'inline-block', background: '#f3f4f6', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginRight: 4, marginBottom: 4 }}>📎 {submissionsCopy.attachmentsPrefix}: {a}</span>
                    ))}
                  </td>
                  <td><span className={`badge ${STATUS_BADGE[s.status] || 'badge-gray'}`}>{submissionsCopy.status[s.status] || s.status}</span></td>
                  <td style={{ color: '#6b7280' }}>{new Date(s.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {s.status === 'pending' && (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={() => approve(s.id)}>{submissionsCopy.actions.approve}</button>
                          <button className="btn btn-sm btn-outline" onClick={() => reject(s.id)}>{submissionsCopy.actions.reject}</button>
                        </>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => remove(s.id)}>{submissionsCopy.actions.delete}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
